/**
 * Unsaved editor text, kept in this browser tab.
 *
 * V1 section 9, first UI acceptance rule:
 *
 *   "Provide loading, empty, no-results, unauthorized, offline/retry,
 *    expired-session, stale-edit and publishing-failed states. Preserve
 *    unsaved text through recoverable failures; never imply a failed save
 *    succeeded."
 *
 * WHICH READING OF SECTION 9 THIS FILE MEETS: THE NARROW ONE.
 *
 *   The rule says *recoverable* failures, and the sentence that follows it
 *   ("never imply a failed save succeeded") is about the write, not about the
 *   browser. Every failure it enumerates is one the person can act on and then
 *   retry from the same tab: offline and back, a refused or 5xx save, a 409
 *   stale edit, a 401 that ends in a reauthentication at the same URL. What
 *   those have in common is that the document is still open in front of them.
 *   For that set, holding the text in memory long enough is nearly enough, and
 *   the gaps are exactly the ones this file closes: an expiry check in
 *   `App.load()` navigates to `/login` (app.ts), and a page reload constructs a
 *   new `App`. Both throw the text away while the tab is still the right place
 *   to put it back.
 *
 * WHY SESSION STORAGE AND NOT indexedDB
 *
 *   sessionStorage is scoped to one tab, is cleared when the tab closes, needs
 *   no server round trip, needs no async plumbing in the input path (a keystroke
 *   handler cannot await, and the editor re-renders on every keystroke), and is
 *   already where the session token lives — so the unsaved text shares the
 *   lifetime of the only credential that could ever write it. Pairs with the
 *   token and it needs an async storage engine on the typing path; that is the
 *   wide reading's bill, and it is not V1's.
 *
 *   The key includes the user id. Signing out of A and into B in the same tab
 *   must not put A's draft reason into B's editor, and with the user in the key
 *   it cannot.
 *
 * KNOWN BOUNDARY — THE WIDE READING IS NOT MET, AND IS NOT MET ON PURPOSE
 *
 *   A browser crash, a tab close, or a machine restart destroys this text. So
 *   does closing the tab after signing back in: sessionStorage does not outlive
 *   its tab. Surviving those needs indexedDB (durable across restarts, but
 *   then it is a cache that can go stale against a real draft, and it outlives
 *   the credential that authored it) or a server-side autosave of the draft
 *   body — and the second is out of scope here for a concrete reason, not a
 *   taste one: `app.drafts` and `app.draft_files` carry no content column
 *   (`draft_files` holds `content_hash` + `storage_key`; the bytes live in the
 *   `skill-drafts` Storage bucket). Autosaving the reason server-side would
 *   change what the database stores and is a migration, a retention decision
 *   and a review question, not a bug fix.
 *
 *   So: unsaved text survives every recoverable failure in this tab, and is
 *   lost when the tab dies. That loss is a boundary, named here so the next
 *   reader does not mistake it for coverage.
 */

const KEY_PREFIX = 'aquifer.unsaved.v1';

/**
 * A `sessionStorage` that may not exist.
 *
 * The module is imported by tests and by a build step, not only by a browser,
 * and reading a missing global throws at import time rather than at the call
 * site that needed it.
 */
function store(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    // Safari in private mode throws on touching `sessionStorage` at all.
    return null;
  }
}

function keyFor(userId: string, draftId: string): string {
  return `${KEY_PREFIX}:${userId}:${draftId}`;
}

/**
 * What this tab is keeping for one draft.
 *
 * TWO FIELDS, ONE KEY. The change reason and the document body are two halves of
 * the same unsaved work, and keeping one while losing the other is not a partial
 * save: it is a document with a reason attached to no content, or a body nobody
 * can explain. They are stored together so they are restored together and
 * cleared together.
 *
 * `source` is the SKILL.md body the editor holds. It is kept for the same reason
 * `note` is: `navigate()` clears both and a new `App` starts both at an empty
 * string, so without it the document body would be the one field that dies on a
 * reload while the reason survived.
 */
export interface UnsavedText {
  readonly note: string;
  readonly source: string;
}

/**
 * Record what this tab is keeping for a draft.
 *
 * The "empty is not worth persisting" rule is applied PER FIELD, not per key.
 * Clearing the change reason must not delete a typed document, so a field is
 * written whatever its value and the key is dropped only when nothing at all is
 * left, which is the one case where there is nothing to lose.
 *
 * JSON, not a delimiter. A SKILL.md body contains every character a document
 * can contain, including whatever separator one would choose.
 */
export function saveUnsaved(userId: string, draftId: string, fields: UnsavedText): void {
  const storage = store();
  if (storage === null) return;
  if (fields.note === '' && fields.source === '') {
    storage.removeItem(keyFor(userId, draftId));
    return;
  }
  storage.setItem(keyFor(userId, draftId), JSON.stringify(fields));
}

/**
 * What this tab kept for this draft, or null if it kept nothing.
 *
 * A key written before this store held two fields is a bare string rather than
 * JSON. It is read as a note with an empty body instead of being discarded, so
 * text kept by the earlier version is recovered rather than silently dropped.
 * A value that parses to neither is discarded, because a corrupt cache entry
 * must not become a failure on a screen whose job is to work.
 */
export function readUnsaved(userId: string, draftId: string): UnsavedText | null {
  const storage = store();
  if (storage === null) return null;
  let value: string | null;
  try {
    value = storage.getItem(keyFor(userId, draftId));
  } catch {
    return null;
  }
  if (value === null || value === '') return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return { note: value, source: '' };
    const record = parsed as { readonly note?: unknown; readonly source?: unknown };
    return {
      note: typeof record.note === 'string' ? record.note : '',
      source: typeof record.source === 'string' ? record.source : '',
    };
  } catch {
    return { note: value, source: '' };
  }
}

/**
 * Drop the kept text.
 *
 * Called only from a success branch. Stale text restored over a fresh draft is
 * worse than lost text: the person would be editing a document that no longer
 * matches what is stored, and the first thing they would see is their own
 * reasoning about content that has since changed.
 */
export function clearUnsaved(userId: string, draftId: string): void {
  const storage = store();
  if (storage === null) return;
  storage.removeItem(keyFor(userId, draftId));
}